import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useInViewReveal } from "@/hooks/useInViewReveal";

const communities = [
  { name: "Los Cabos, MX.", flag: "🇲🇽" },
  { name: "Oxford, ENG.", flag: "🇬🇧" },
  { name: "Quarteira, PT.", flag: "🇵🇹" },
  { name: "Lisboa, PT.", flag: "🇵🇹" },
];

const useCountUp = (target: number, duration: number, start: boolean) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setCount(target);
      return;
    }

    let startTime: number | null = null;
    let animationFrame: number;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // Ease out
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      }
    };

    animationFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrame);
  }, [target, duration, start]);

  return count;
};

export const WhoWeCanPaySection = () => {
  const [t] = useTranslation("homeowners");
  const { ref: sectionRef, hasRevealed } = useInViewReveal({ threshold: 0.2 });
  const [counterDone, setCounterDone] = useState(false);
  const count = useCountUp(40, 600, hasRevealed);

  const onCounterEnd = useCallback(() => {
    if (count === 40 && !counterDone) setCounterDone(true);
  }, [count, counterDone]);

  useEffect(() => {
    onCounterEnd();
  }, [onCounterEnd]);

  return (
    <section
      id="who-we-can-pay"
      ref={sectionRef}
      className="w-full bg-white px-6 sm:px-12 lg:px-24 pt-16 lg:pt-24 pb-20 lg:pb-32"
    >
      {/* Two-column layout */}
      <div
        className={classNames(
          "flex flex-col lg:flex-row gap-12 lg:gap-16 xl:gap-24 transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {/* Left column — Communities */}
        <div className="flex-1">
          <h3 className="font-minion text-homeowners-headline leading-snug text-[clamp(1.5rem,0.8rem+3vw,3.75rem)]">
            {t("WhoWeCanPay.Title")}
          </h3>
          <p className="mt-6 lg:mt-8 font-montserrat font-normal text-[#1a1a1a] leading-relaxed text-[clamp(0.875rem,0.7rem+0.6vw,1.5rem)]">
            {t("WhoWeCanPay.Desc")}
          </p>

          {/* Community tags */}
          <div className="mt-8 lg:mt-12 flex flex-wrap gap-4 lg:gap-5">
            {communities.map((community) => (
              <div
                key={community.name}
                className="flex items-center gap-3 bg-white rounded-xl px-5 py-3 lg:px-6 lg:py-4"
              >
                <span className="text-2xl lg:text-3xl leading-none">
                  {community.flag}
                </span>
                <span className="font-montserrat font-normal text-[#1a1a1a] text-sm lg:text-base xl:text-lg">
                  {community.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column — 40+ countries card */}
        <div className="lg:w-[380px] xl:w-[480px] flex-shrink-0">
          <div className="bg-[#e3f1ea] rounded-[48px] flex flex-col items-center gap-8 lg:gap-12 px-8 lg:px-12 py-12 lg:py-16 text-[#1a1a1a] text-center">
            <p className="font-montserrat font-normal text-[clamp(1rem,0.8rem+0.8vw,2rem)] leading-snug">
              {t("WhoWeCanPay.SupportTitle")}
            </p>

            {/* Counter — animation #9 */}
            <div className="flex items-end gap-2">
              <span
                className={classNames(
                  "font-playfair font-bold text-[clamp(4rem,3rem+5vw,8rem)] leading-none transition-transform duration-300",
                  { "scale-[1.02]": counterDone }
                )}
              >
                {hasRevealed ? count : 0}+
              </span>
              <span className="font-montserrat font-semibold text-[clamp(1.25rem,1rem+1vw,2.25rem)] leading-none pb-2 lg:pb-4">
                {t("WhoWeCanPay.CounterLabel")}
              </span>
            </div>

            <p className="font-montserrat font-normal text-[clamp(0.875rem,0.7rem+0.5vw,1.5rem)] leading-snug">
              {t("WhoWeCanPay.SupportDesc")}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom text */}
      <p
        className={classNames(
          "mt-16 lg:mt-24 font-montserrat font-normal text-[#1a1a1a] text-center leading-relaxed text-[clamp(0.875rem,0.7rem+0.8vw,1.75rem)] transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] delay-[160ms]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {t("WhoWeCanPay.BottomText")}
      </p>
    </section>
  );
};
